import axios from "axios";
import Config from "../../Config";
import {
  createDefaultStoreConfig,
  normalizeStoreConfig,
  prepareStorePayloadForApi,
} from "../../utils/storeDefaults";
import { loadStoreFromStorage, saveStoreToStorage } from "../../utils/storeStorage";

class StoreService {
  static storeContentUrl = `${Config.backendDomain}vendor/store-content`;

  static getHeaders() {
    return {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("dealboxx_token")}`,
      },
    };
  }

  static getResponseEnvelope(response) {
    return response?.data?.data ?? response?.data ?? null;
  }

  static isApiSuccess(envelope) {
    if (!envelope) return false;
    const code = envelope.status_code;
    return (code === "000" || code === "001") && envelope.in_error !== true;
  }

  static hasServerRecord(data) {
    if (!data || typeof data !== "object") return false;
    return Boolean(
      data.id ||
        data.store_content_id ||
        data.created_at ||
        data.updated_at
    );
  }

  static parseStoreResponse(response, fallback = {}) {
    const envelope = StoreService.getResponseEnvelope(response);
    if (!StoreService.isApiSuccess(envelope)) {
      return null;
    }

    const storeData = envelope.data ?? fallback;
    return {
      success: true,
      data: normalizeStoreConfig(storeData),
      source: "api",
      existsOnServer: StoreService.hasServerRecord(storeData),
      message: envelope.reason,
    };
  }

  static isNotFoundError(error) {
    return (
      error?.response?.status === 404 ||
      error?.response?.data?.data?.reason?.includes("could not be found") ||
      error?.response?.data?.data?.status_code === "404"
    );
  }

  static getErrorMessage(error, fallback = "Request failed.") {
    return (
      error?.response?.data?.data?.reason ||
      error?.response?.data?.message ||
      error?.message ||
      fallback
    );
  }

  static async getStoreSettings(vendorId) {
    try {
      const response = await axios.get(
        StoreService.storeContentUrl,
        StoreService.getHeaders()
      );

      const parsed = StoreService.parseStoreResponse(response);
      if (parsed) {
        return {
          ...parsed,
          existsOnServer: parsed.existsOnServer ?? StoreService.hasServerRecord(parsed.data),
        };
      }

      const envelope = StoreService.getResponseEnvelope(response);
      console.warn("Unexpected store content GET response:", envelope);
    } catch (error) {
      if (StoreService.isNotFoundError(error)) {
        return {
          success: true,
          data: createDefaultStoreConfig(),
          source: "api",
          existsOnServer: false,
        };
      }

      console.warn("Store content API unavailable, using local storage.", error);
    }

    return {
      success: true,
      data: loadStoreFromStorage(vendorId),
      source: "local",
      existsOnServer: false,
    };
  }

  static async saveStoreSettings(vendorId, config, { existsOnServer = false } = {}) {
    const payload = prepareStorePayloadForApi(config);

    const requestSave = async (method) => {
      const response = await axios[method](
        StoreService.storeContentUrl,
        payload,
        StoreService.getHeaders()
      );

      const parsed = StoreService.parseStoreResponse(response, payload);
      if (parsed) {
        return {
          ...parsed,
          existsOnServer: true,
        };
      }

      const envelope = StoreService.getResponseEnvelope(response);
      throw new Error(envelope?.reason || "Failed to save store content.");
    };

    try {
      if (existsOnServer) {
        try {
          return await requestSave("put");
        } catch (error) {
          if (StoreService.isNotFoundError(error)) {
            return await requestSave("post");
          }
          throw error;
        }
      }

      return await requestSave("post");
    } catch (error) {
      if (!error?.response) {
        const saved = saveStoreToStorage(vendorId, config);
        return {
          success: true,
          data: saved,
          source: "local",
          existsOnServer: false,
          message: "Saved offline. Will sync when you're back online.",
        };
      }

      return {
        success: false,
        message: StoreService.getErrorMessage(error, "Failed to save store content."),
        source: "api",
      };
    }
  }
}

export default StoreService;
